/**
 * Offline mutation queue (ADR 0005 Phase 1).
 *
 * When a shelf mutation fails because the network is unreachable, the client
 * stores it here (localStorage, so it survives the tab closing) and replays
 * it once connectivity returns — see `flushQueue` in `client.ts`.
 *
 * Only writes sent as `queued` (client.ts) are kept: the shelf's changes.
 * Replaying a sign-in, a catalog write or a comment later would be
 * surprising, but a status change, log or reorder is exactly what "log it
 * on the couch, sync it later" means.
 *
 * Each mutation remembers whose it is, so one that's still queued when its
 * user logs out waits for them rather than landing on the next account to
 * sign in on the device.
 */

export type QueuedMutation = {
  path: string;
  method: string;
  body?: unknown;
  queuedAt: string;
  // Missing on mutations queued before users were recorded.
  userId?: number | null;
};

const QUEUE_KEY = "khipur_offline_queue";

export const readQueue = (): QueuedMutation[] => {
  const raw = localStorage.getItem(QUEUE_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as QueuedMutation[];
  } catch {
    // A corrupt queue can't be replayed — drop it rather than wedge every
    // future mutation behind a parse error.
    localStorage.removeItem(QUEUE_KEY);
    return [];
  }
};

const write = (queue: QueuedMutation[]): void => {
  if (queue.length === 0) {
    localStorage.removeItem(QUEUE_KEY);
  } else {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  }
};

export const enqueue = (mutation: Omit<QueuedMutation, "queuedAt">): void => {
  write([...readQueue(), { ...mutation, queuedAt: new Date().toISOString() }]);
};

/**
 * Take a user's mutations off the queue, returning them (oldest first);
 * other users' stay queued. Mutations queued before users were recorded go
 * to whoever replays first, as they always did.
 */
export const drainQueue = (userId: number | null): QueuedMutation[] => {
  const queue = readQueue();
  const mine = (m: QueuedMutation) =>
    m.userId === undefined || m.userId === userId;
  write(queue.filter((m) => !mine(m)));
  return queue.filter(mine);
};
